import {
  useEffect,
  useState,
  useRef,
  useMemo } from
'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SofaIcon,
  BedIcon,
  UtensilsIcon,
  BathIcon,
  ChevronDownIcon,
  ShirtIcon,
  AirVentIcon,
  DoorOpenIcon,
  LayoutGridIcon } from
'lucide-react';
// Components
import { LoginScreen } from '../components/LoginScreen';
import { UnitSelectionScreen } from '../components/UnitSelectionScreen';
// Components
import { SystemStatusBar } from '../components/SystemStatusBar';
import { SettingsModal } from '../components/SettingsModal';
import { IssueBoardModal } from '../components/IssueBoardModal';
import { RoomZonesSidebar } from '../components/RoomZonesSidebar';
import { FloorPlanHeatMap } from '../components/FloorPlanHeatMap';
import { FloorPlanEditor, EditorZone, EditorSensor } from '../components/FloorPlanEditor';
import { EnergyChart } from '../components/EnergyChart';
import { MonthlyEnergyWidget } from '../components/MonthlyEnergyWidget';
import { TopOutletWidget } from '../components/TopOutletWidget';
import { ZoneDetailSidebar } from '../components/ZoneDetailSidebar';
import { SensorDetailSidebar } from '../components/SensorDetailSidebar';
import { Sensor } from '../components/FloorPlanHeatMap';
import { ViewMode, VIEW_MODES } from '../utils/viewModeUtils';
import { useEditorZones } from '../hooks/useEditorZones';
import { useAlarms } from '../hooks/useAlarms';
import { thingsboardService } from '../services/thingsboard';
import { CurtainController } from '../components/CurtainController';
import { ProportionalContainer } from '../components/ProportionalContainer';
import { getBoundingBox } from '../utils/polygonUtils';

  // Initialized from UnitSelectionScreen
// INITIAL_EDITOR_ZONES removed and now injected by useEditorZones hook

const ROOM_ICONS: Record<string, any> = {
  '거실': SofaIcon,
  '침실': BedIcon,
  '침실1': BedIcon,
  '침실2': BedIcon,
  '안방': BedIcon,
  '주방': UtensilsIcon,
  '화장실': BathIcon,
  '화장실1': BathIcon,
  '화장실2': BathIcon,
  '드레스룸': ShirtIcon,
  '실외기실': AirVentIcon,
  '현관': DoorOpenIcon,
  '알파룸': LayoutGridIcon
};



export function TabletApp() {
  const { t } = useTranslation();
  const [selectedRoomId, setSelectedRoomId] = useState('living');
  const [viewMode, setViewMode] = useState<ViewMode>('energy');
  const [isLoaded, setIsLoaded] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('jwtToken'));
  const [apartmentAssetId, setApartmentAssetId] = useState<string | null>(localStorage.getItem('apartmentAssetId'));
  const [apartmentLabel, setApartmentLabel] = useState<string | null>(localStorage.getItem('apartmentLabel'));
  const [isZoomed, setIsZoomed] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [seniorCareEnabled, setSeniorCareEnabled] = useState(false);
  const [absenceAlarmEnabled, setAbsenceAlarmEnabled] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isIssueBoardOpen, setIsIssueBoardOpen] = useState(false);

  // Splash Screen Effect
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Force dark mode for Tablet version
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  const handleLogin = (token: string) => {
    setApartmentAssetId(null);
    setApartmentLabel(null);
    localStorage.removeItem('apartmentAssetId');
    localStorage.removeItem('apartmentLabel');
    setAuthToken(token);
    localStorage.setItem('jwtToken', token);
  };

  const handleUnitSelect = (assetId: string, assetLabel: string) => {
    setApartmentAssetId(assetId);
    setApartmentLabel(assetLabel);
    localStorage.setItem('apartmentAssetId', assetId);
    localStorage.setItem('apartmentLabel', assetLabel);
  };

  const handleLogout = () => {
    thingsboardService.logout();
    setAuthToken(null);
    setApartmentAssetId(null);
    setApartmentLabel(null);
    localStorage.removeItem('apartmentAssetId');
    localStorage.removeItem('apartmentLabel');
  };

  const handleChangeSpace = () => {
    setApartmentAssetId(null);
    setApartmentLabel(null);
    localStorage.removeItem('apartmentAssetId');
    localStorage.removeItem('apartmentLabel');
  };

  const currentAssetId = apartmentAssetId;

  useEffect(() => {
    const fetchSeniorCareState = async () => {
      if (!currentAssetId || currentAssetId === '00000000-0000-0000-0000-000000000000') return;
      try {
        const attrs = await thingsboardService.getEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', ['senior_care_enabled', 'absence_alarm_enabled']);
        if (attrs && attrs.length > 0) {
          const seniorAttr = attrs.find((a: any) => a.key === 'senior_care_enabled');
          if (seniorAttr) setSeniorCareEnabled(String(seniorAttr.value) === 'true');
          
          const absenceAttr = attrs.find((a: any) => a.key === 'absence_alarm_enabled');
          if (absenceAttr) setAbsenceAlarmEnabled(String(absenceAttr.value) === 'true');
        }
      } catch (err) {
        console.error('Failed to fetch senior care state from ThingsBoard:', err);
      }
    };
    if (currentAssetId) {
      fetchSeniorCareState();
    }
  }, [currentAssetId]);

  const handleToggleSeniorCare = async () => {
    const newState = !seniorCareEnabled;
    setSeniorCareEnabled(newState); // Optimistic UI update
    
    // Automatically turn off absence alarm if senior care is turned off
    const newAbsenceState = newState ? absenceAlarmEnabled : false;
    if (!newState && absenceAlarmEnabled) {
      setAbsenceAlarmEnabled(false);
    }
    
    if (!currentAssetId || currentAssetId === '00000000-0000-0000-0000-000000000000') return;
    try {
      await thingsboardService.saveEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', {
        senior_care_enabled: newState,
        absence_alarm_enabled: newAbsenceState
      });
    } catch (err) {
      console.error('Failed to save senior care state to ThingsBoard:', err);
      setSeniorCareEnabled(!newState); // Revert on failure
      if (!newState && absenceAlarmEnabled) {
        setAbsenceAlarmEnabled(true);
      }
    }
  };

  const handleToggleAbsenceAlarm = async () => {
    if (!seniorCareEnabled) return; // Cannot toggle if senior care is off
    
    const newState = !absenceAlarmEnabled;
    setAbsenceAlarmEnabled(newState); // Optimistic UI update
    if (!currentAssetId || currentAssetId === '00000000-0000-0000-0000-000000000000') return;
    try {
      await thingsboardService.saveEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', {
        absence_alarm_enabled: newState
      });
    } catch (err) {
      console.error('Failed to save absence alarm state to ThingsBoard:', err);
      setAbsenceAlarmEnabled(!newState); // Revert on failure
    }
  };

  const [floorPlanUrl, setFloorPlanUrl] = useState<string | null>(null);
  const [homeName, setHomeName] = useState<string | null>(null);
  // Fetch dynamic floorplan URL
  useEffect(() => {
    async function fetchFloorPlanUrl() {
      if (!currentAssetId || currentAssetId === '00000000-0000-0000-0000-000000000000') {
        setFloorPlanUrl(null);
        return;
      }
      try {
        const attrs = await thingsboardService.getEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', ['floorplan','homeName']);
        if (attrs && attrs.length > 0) {
          const fpAttr = attrs.find((a: any) => a.key === 'floorplan');
          if (fpAttr && fpAttr.value) {
            setFloorPlanUrl(`https://iot.seqsys.net${fpAttr.value}`);
          }
          const homeNameAttr = attrs.find((a: any) => a.key === 'homeName');
          if (homeNameAttr && homeNameAttr.value) {
            setHomeName(homeNameAttr.value);
          }
        }
      } catch (err) {
        console.error('Failed to fetch floorplan attribute:', err);
      }
    }
    fetchFloorPlanUrl();
  }, [currentAssetId]);
  
  const { zones: editorZones, primaryZoneId: fetchedPrimaryZoneId, setZones: setEditorZones, loading: zonesLoading, error: zonesError, refetch: refetchEditorZones } = useEditorZones(currentAssetId);
  const { messages: alarmMessages } = useAlarms(currentAssetId);
  const [primaryZoneId, setPrimaryZoneId] = useState<string | null>(null);

  useEffect(() => {
    if (fetchedPrimaryZoneId !== undefined) {
      setPrimaryZoneId(fetchedPrimaryZoneId);
    }
  }, [fetchedPrimaryZoneId]);
  const dynamicRooms = useMemo(() => {
    if (!editorZones || editorZones.length === 0) {
      // Return empty array if no zones exist
      return [];
    }
    return editorZones.map(zone => {
       const mappedIcon = ROOM_ICONS[zone.name];
       return {
         id: zone.id,
         name: zone.name,
         temp: 24, // Default baseline temperature simulation
         icon: mappedIcon ? mappedIcon : LayoutGridIcon
       };
    });
  }, [editorZones]);

  useEffect(() => {
    if (editorZones.length > 0 && selectedRoomId === 'living') {
      let defaultRoom = dynamicRooms[0];
      if (primaryZoneId) {
        defaultRoom = dynamicRooms.find(r => r.id === primaryZoneId) || defaultRoom;
      } else {
        defaultRoom = dynamicRooms.find(r => r.name === '거실') || defaultRoom;
      }
      if (defaultRoom) {
        setSelectedRoomId(defaultRoom.id);
      }
    } else if (editorZones.length === 0) {
      setSelectedRoomId('');
    }
  }, [editorZones, dynamicRooms, selectedRoomId, primaryZoneId]);
  
  const allSeoSensors = useMemo(() => {
    return editorZones.flatMap((zone) => 
      zone.sensors
        .filter((s) => s.type === 'SEO' || s.type === 'SEM' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET')
        .map((s) => ({ ...s, roomId: zone.id, roomName: zone.name }))
    );
  }, [editorZones]);
  
 
   // Device toast state
  const [deviceToast, setDeviceToast] = useState<{
    name: string;
    alias?: string;
  } | null>(null);

  const allSensors = useMemo(() => {
    return editorZones.flatMap(z => z.sensors);
  }, [editorZones]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setDeviceToast({
        name: detail.name,
        alias: detail.alias
      });
    };
    window.addEventListener('device-toast', handler);
    return () => window.removeEventListener('device-toast', handler);
  }, []);

  // Handle ThingsBoard Authentication Failure (Token Refresh Failed)
  useEffect(() => {
    const handleAuthFailed = () => {
      console.warn('ThingsBoard authentication failed. Logging out...');
      handleLogout();
    };
    window.addEventListener('tb-auth-failed', handleAuthFailed);
    return () => window.removeEventListener('tb-auth-failed', handleAuthFailed);
  }, []);

  // Fullscreen state
  const [isFullscreen] = useState(false); // Used selectively with gestures but no longer tightly coupled to browser fullscreen API
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const pullThreshold = 120;
  // Pull-down to exit fullscreen gesture
  useEffect(() => {
    if (!isFullscreen) {
      setPullDistance(0);
      setIsPulling(false);
      return;
    }
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      // Only activate if touch starts in the top 80px of the screen
      if (touch.clientY < 80) {
        touchStartY.current = touch.clientY;
        setIsPulling(true);
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === null) return;
      const touch = e.touches[0];
      const distance = Math.max(0, touch.clientY - touchStartY.current);
      setPullDistance(distance);
      if (distance > 10) {
        e.preventDefault();
      }
    };
    const handleTouchEnd = () => {
      if (pullDistance >= pullThreshold) {
        document.exitFullscreen().catch(() => {});
      }
      touchStartY.current = null;
      setPullDistance(0);
      setIsPulling(false);
    };
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    document.addEventListener('touchmove', handleTouchMove, {
      passive: false
    });
    document.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isFullscreen, pullDistance]);
  const currentRoom = dynamicRooms.find((r) => r.id === selectedRoomId) || dynamicRooms[0] || null;
  const [displayTemp, setDisplayTemp] = useState(currentRoom ? currentRoom.temp : 0);
  useEffect(() => {
    const interval = setInterval(() => {
      if (!currentRoom) return;
      const variance = Math.random() > 0.5 ? 0.1 : -0.1;
      if (Math.abs(displayTemp - currentRoom.temp) < 1) {
        setDisplayTemp((prev) => Number((prev + variance).toFixed(1)));
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [currentRoom, displayTemp]);
  useEffect(() => {
    setDisplayTemp(currentRoom ? currentRoom.temp : 0);
  }, [selectedRoomId, currentRoom]);
  const handleRoomSelect = (id: string) => {
    setSelectedRoomId(id);
    setIsZoomed(true);
    setSelectedSensor(null);
  };
  const handleZoomOut = () => {
    setIsZoomed(false);
    setSelectedRoomId(null);
    setSelectedSensor(null);
  };
  const handleSensorSelect = (sensor: Sensor) => {
    if (sensor.type === 'SEC' || sensor.type === 'CURTAIN' || sensor.type === 'BLIND') {
      window.dispatchEvent(
        new CustomEvent('device-toast', { detail: { name: sensor.label, alias: sensor.alias } })
      );
      return;
    }
    setSelectedSensor(sensor);
  };
  const handleSensorBack = () => {
    setSelectedSensor(null);
  };
  const handleOpenEditor = () => {
    setIsEditorOpen(true);
  };
  const handleCloseEditor = () => {
    setIsEditorOpen(false);
  };
  const handleSaveEditor = async (zones: EditorZone[], assignedSensors: { originalZoneId: string | null; sensor: EditorSensor; newZoneId: string | null }[]) => {
    if (!currentAssetId || currentAssetId === '00000000-0000-0000-0000-000000000000') {
      console.error('Cannot save new entities: currentAssetId is missing');
      throw new Error('선택된 공간(호) 정보가 없어 저장할 수 없습니다.');
    }

    try {
      // Create a mutable copy of the updated zones to inject real DB UUIDs later
      const finalizedZones = JSON.parse(JSON.stringify(zones)) as EditorZone[];

      // Handle deleted zones
      const currentZoneIds = new Set(finalizedZones.map(z => z.id));
      const deletedZones = editorZones.filter(z => !currentZoneIds.has(z.id));
      
      for (const deletedZone of deletedZones) {
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(deletedZone.id)) {
          try {
            console.log(`Deleting Zone Asset from TB: ${deletedZone.name} (${deletedZone.id})`);
            await thingsboardService.deleteRelation(currentAssetId, 'ASSET', deletedZone.id, 'ASSET', 'Contains').catch(() => {});
            await thingsboardService.deleteAsset(deletedZone.id).catch(() => {});
          } catch (err) {
            console.error(`Failed to delete zone ${deletedZone.id}:`, err);
          }
        }
      }

      const zoneIdMap = new Map<string, string>();

      // 1. Process Zones (Create if new, Update attributes)
      for (let i = 0; i < finalizedZones.length; i++) {
        let zone = finalizedZones[i];
        let zoneUuid = zone.id;

        const isNewZone = !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(zone.id);

        try {
          if (isNewZone) {
            console.log(`Creating new Zone Asset in TB: ${zone.name}`);
            const newAssetRef = await thingsboardService.createAsset(zone.name, 'zone');
            zoneUuid = newAssetRef.id.id; // Extract ThingsBoard UUID

            // Relate new Zone Asset to Apartment Asset (Apartment -> Contains -> Zone)
            await thingsboardService.createRelation(currentAssetId, 'ASSET', zoneUuid, 'ASSET', 'Contains');
            
            zoneIdMap.set(zone.id, zoneUuid);
            zone.id = zoneUuid;
          }

          const bbox = zone.points && zone.points.length >= 3 
            ? getBoundingBox(zone.points)
            : { top: zone.top, left: zone.left, width: zone.width, height: zone.height };

          zone.top = bbox.top;
          zone.left = bbox.left;
          zone.width = bbox.width;
          zone.height = bbox.height;

          const pointsJson = JSON.stringify(zone.points || []);
          const perimeterArray = (zone.points || []).map(p => [
            Number((p.x / 100).toFixed(6)),
            Number((p.y / 100).toFixed(6))
          ]);
          const zoneAttrs: Record<string, any> = {
            top: bbox.top,
            left: bbox.left,
            width: bbox.width,
            height: bbox.height,
            points: pointsJson,
            polygon: pointsJson,
            perimeter: JSON.stringify(perimeterArray)
          };
          if (zone.primary_sensor !== undefined && zone.primary_sensor !== null) {
            zoneAttrs.primary_sensor = zone.primary_sensor;
          }

          await thingsboardService.saveEntityAttributes('ASSET', zoneUuid, 'SERVER_SCOPE', zoneAttrs);
        } catch (err) {
          console.error(`Failed to save zone attributes for ${zoneUuid}`, err);
          throw err;
        }
      }

      // 2. Handle newly assigned and removed sensors (Existing Sensors Only)
      for (const assignment of assignedSensors) {
        const sensorId = assignment.sensor.id;
        const isExistingSensor = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sensorId);

        if (isExistingSensor) {
          const originalZoneId = assignment.originalZoneId ? (zoneIdMap.get(assignment.originalZoneId) || assignment.originalZoneId) : null;
          const newZoneId = assignment.newZoneId ? (zoneIdMap.get(assignment.newZoneId) || assignment.newZoneId) : null;

          try {
            if (!originalZoneId && newZoneId && currentAssetId) {
              // Moved from Apartment to Zone
              await thingsboardService.deleteRelation(currentAssetId, 'ASSET', sensorId, 'DEVICE', 'Contains').catch(() => {});
              await thingsboardService.createRelation(newZoneId, 'ASSET', sensorId, 'DEVICE', 'Contains');
            } else if (originalZoneId && !newZoneId && currentAssetId) {
              // Moved from Zone to Apartment (Removed)
              await thingsboardService.deleteRelation(originalZoneId, 'ASSET', sensorId, 'DEVICE', 'Contains').catch(() => {});
              await thingsboardService.createRelation(currentAssetId, 'ASSET', sensorId, 'DEVICE', 'Contains');
            } else if (originalZoneId && newZoneId && originalZoneId !== newZoneId) {
              // Moved between Zones
              await thingsboardService.deleteRelation(originalZoneId, 'ASSET', sensorId, 'DEVICE', 'Contains').catch(() => {});
              await thingsboardService.createRelation(newZoneId, 'ASSET', sensorId, 'DEVICE', 'Contains');
            }
          } catch (err) {
            console.error(`Failed to update relations for sensor ${sensorId}`, err);
          }
        }
      }

      // 3. Process Sensors per Zone (Create new sensors, Update attributes)
      for (let i = 0; i < finalizedZones.length; i++) {
        let zone = finalizedZones[i];
        let zoneUuid = zone.id;

        for (let j = 0; j < zone.sensors.length; j++) {
          let sensor = zone.sensors[j];
          let sensorUuid = sensor.id;
          
          const isNewSensor = !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sensor.id);

          try {
            if (isNewSensor) {
              console.log(`Creating new Sensor Device in TB: ${sensor.label}`);
              const newDeviceRef = await thingsboardService.createDevice(
                sensor.label, // Device name maps to label in UI
                sensor.type,  // Device profile name maps to type in UI
                ''            // We don't need a separate label attribute
              );
              sensorUuid = newDeviceRef.id.id;

              // Relate new Sensor Device to Zone Asset (Zone -> Contains -> Sensor)
              await thingsboardService.createRelation(zoneUuid, 'ASSET', sensorUuid, 'DEVICE', 'Contains');

              // Replace local ID with DB ID
              sensor.id = sensorUuid;
            }

            // Save Sensor Coordinates and Metadata
            const sensorAttrs = {
              active: sensor.active,
              x: sensor.x,
              y: sensor.y
            };
            await thingsboardService.saveEntityAttributes('DEVICE', sensorUuid, 'SERVER_SCOPE', sensorAttrs);
          } catch (err) {
            console.error(`Failed to save attributes for sensor ${sensorUuid}`, err);
          }
        }
      }

      // 4. Update the primary zone globally if a valid currentAssetId exists
      if (currentAssetId && currentAssetId !== '00000000-0000-0000-0000-000000000000') {
        const payload = {
          primaryZoneId: primaryZoneId || ''
        };
        await thingsboardService.saveEntityAttributes('ASSET', currentAssetId, 'SERVER_SCOPE', payload);
      }

      // Update UI state with finalized Database UUIDs & reload from ThingsBoard
      setEditorZones(finalizedZones);
      await refetchEditorZones();
      console.log('Successfully saved floor plan mapping to ThingsBoard including new entity creation');
    } catch (error) {
      console.error('Error saving mapping to ThingsBoard:', error);
      throw error;
    }
  };
  
  // 1. Loading Splash
  if (!isLoaded) {
    return (
      <ProportionalContainer>
        <div className="h-full w-full bg-[#0a0f1a] flex flex-col items-center justify-center gap-8">
          <motion.div
             initial={{ opacity: 0, scale: 0.9 }}
             animate={{ opacity: 1, scale: 1 }}
             className="flex flex-col items-center"
          >
            <img src="/remove_background_fr.png" alt="Samsung" className="w-64 h-auto mb-6 opacity-80" />
            <div className="text-sm font-mono tracking-[0.3em] text-slate-500 uppercase">Smart Edge Device Dashboard</div>
          </motion.div>
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 border-4 border-cyan-500/10 border-t-cyan-500 rounded-full"
          />
        </div>
      </ProportionalContainer>
    );
  }

  // 2. Login Screen
  if (!authToken) {
    return (
      <ProportionalContainer>
        <LoginScreen onLogin={handleLogin} />
      </ProportionalContainer>
    );
  }

  // 3. Asset Selection (Ho)
  if (!apartmentAssetId) {
    return (
      <ProportionalContainer>
        <UnitSelectionScreen 
          onSelect={handleUnitSelect} 
          onLogout={handleLogout} 
        />
      </ProportionalContainer>
    );
  }

  return (
    <ProportionalContainer>
      <div className="h-full w-full bg-[#0a0f1a] text-slate-200 font-sans selection:bg-cyan-500/30 flex flex-col overflow-hidden">
      
      {/* Loading Overlay from Zones Hook */}
      <AnimatePresence>
        {isLoaded && zonesLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-[#0a0f1a]/80 backdrop-blur-sm flex flex-col items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin" />
            <div className="mt-4 text-cyan-400 font-roboto text-sm tracking-widest">
              {t('app.loadingFloorplan')}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isLoaded && zonesError && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed top-12 transform -translate-x-1/2 left-1/2 z-[160] bg-rose-500/20 border border-rose-500/50 backdrop-blur-md text-rose-200 px-4 py-3 rounded-lg flex items-start max-w-[80vw]">
            <div className="w-5 h-5 mt-0.5 rounded-full bg-rose-500/20 flex items-center justify-center mr-3 flex-shrink-0">
              <span className="text-rose-400 text-xs font-bold">!</span>
            </div>
            <div className="text-sm shadow-black drop-shadow-md">
              <strong className="block mb-1 font-bold">{t('app.errorLoadingFloorplan')}</strong>
              <span className="opacity-90 leading-relaxed font-roboto text-xs block">{zonesError}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pull-down indicator overlay (fullscreen only) */}
      <AnimatePresence>
        {isFullscreen && isPulling && pullDistance > 5 &&
        <motion.div
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0
          }}
          className="fixed inset-x-0 top-0 z-[100] flex flex-col items-center pointer-events-none"
          style={{
            height: `${Math.min(pullDistance, pullThreshold + 40)}px`
          }}>

            {/* Gradient backdrop */}
            <div
            className="absolute inset-0 bg-gradient-to-b from-cyan-500/20 via-cyan-500/5 to-transparent"
            style={{
              opacity: Math.min(pullDistance / pullThreshold, 1)
            }} />

            {/* Pull indicator */}
            <motion.div
            className="relative z-10 flex flex-col items-center gap-1 mt-2"
            animate={{
              y: Math.min(pullDistance * 0.4, pullThreshold * 0.4),
              scale: pullDistance >= pullThreshold ? 1.1 : 1
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }}>

              <ChevronDownIcon
              className={`w-5 h-5 transition-colors duration-200 ${pullDistance >= pullThreshold ? 'text-cyan-400' : 'text-slate-400'}`} />

              <span
              className={`text-[10px] font-roboto uppercase tracking-widest transition-colors duration-200 ${pullDistance >= pullThreshold ? 'text-cyan-400' : 'text-slate-500'}`}>

                {pullDistance >= pullThreshold ?
              t('app.releaseToExit') :
              t('app.pullToExit')}
              </span>
              {/* Progress bar */}
              <div className="w-16 h-0.5 bg-white/10 rounded-full overflow-hidden mt-1">
                <motion.div
                className={`h-full rounded-full ${pullDistance >= pullThreshold ? 'bg-cyan-400' : 'bg-slate-500'}`}
                style={{
                  width: `${Math.min(pullDistance / pullThreshold * 100, 100)}%`
                }} />

              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>

      {/* Background Grid Effect */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-20"
        style={{
          backgroundImage:
          'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />

      <div className="fixed inset-0 pointer-events-none z-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a0f1a_100%)]" />

        <SystemStatusBar 
          isSettingsOpen={isSettingsOpen} 
          onOpenSettings={() => setIsSettingsOpen(true)} 
          isIssueBoardOpen={isIssueBoardOpen}
          onOpenIssueBoard={() => setIsIssueBoardOpen(true)}
          apartmentLabel={apartmentLabel || ''}
          onChangeSpace={handleChangeSpace}
          hasAlarms={alarmMessages.length > 0}
          apartmentAssetId={currentAssetId}
        />


      <div className="flex-1 flex flex-row overflow-hidden relative z-10 min-h-0">
        {/* Left Sidebar */}
        <RoomZonesSidebar
          selectedRoom={selectedRoomId}
          onSelectRoom={handleRoomSelect}
          rooms={dynamicRooms}
          primaryZoneId={primaryZoneId}
          primaryZoneName={primaryZoneId ? dynamicRooms.find(r => r.id === primaryZoneId)?.name : undefined}
          humidity={0}
          aqi={0}
          currentAssetId={currentAssetId}
          homeName={homeName || ''} />


        {/* Main Content Area */}
        <main className="w-[35%] flex-shrink-0 flex flex-col p-3 overflow-hidden min-w-0">
          <div className="w-full h-full flex flex-col min-h-0">
            {/* Header */}
            <motion.div
              initial={{
                opacity: 0,
                y: -20
              }}
              animate={{
                opacity: 1,
                y: 0
              }}
              className="mb-2 flex items-center justify-between flex-shrink-0">

              {/* View Mode Toggle Buttons */}
              <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1 border border-white/5 w-full">
                {VIEW_MODES.map((vm) => {
                  const isActive = viewMode === vm.id;
                  const Icon = vm.icon;
                  return (
                    <button
                      key={vm.id}
                      onClick={() => setViewMode(vm.id)}
                      className={`
                        relative flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[0.9rem] font-medium uppercase tracking-wider transition-all duration-300
                        ${isActive ? 'bg-white/10 shadow-sm ' + vm.activeColor : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}
                      `}>

                      {isActive &&
                      <motion.div
                        layoutId="viewModeIndicator"
                        className="absolute inset-0 bg-white/10 rounded-lg"
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 30
                        }} />

                      }
                      <Icon className="w-3.5 h-3.5 relative z-10" />
                      <span className="relative z-10">{t(vm.label)}</span>
                    </button>);

                })}
              </div>
            </motion.div>

            {/* Floor Plan Heat Map */}
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95
              }}
              animate={{
                opacity: 1,
                scale: 1
              }}
              transition={{
                duration: 0.5
              }}
              className="flex-1 flex flex-col items-center glass-panel rounded-2xl p-3 relative overflow-hidden min-h-0">

              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" />
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" />

              <div className="relative z-10 flex flex-col items-center w-full h-full min-h-0">
                <div className="flex-1 w-full flex items-center justify-center overflow-hidden min-h-0">
                  <FloorPlanHeatMap
                    rooms={dynamicRooms}
                    selectedRoomId={selectedRoomId}
                    onSelectRoom={handleRoomSelect}
                    isZoomed={isZoomed}
                    onZoomOut={handleZoomOut}
                    onSelectSensor={handleSensorSelect}
                    selectedSensorId={selectedSensor?.id}
                    viewMode={viewMode}
                    seniorCareEnabled={seniorCareEnabled}
                    onToggleSeniorCare={handleToggleSeniorCare}
                    absenceAlarmEnabled={absenceAlarmEnabled}
                    onToggleAbsenceAlarm={handleToggleAbsenceAlarm}
                    onOpenEditor={handleOpenEditor}
                    zoneOverrides={editorZones.map(z => ({
                      ...z,
                      sensors: z.sensors.map(s => ({ ...s, roomId: z.id }))
                    }))}
                    currentAssetId={currentAssetId}
                    floorPlanUrl={floorPlanUrl} />

                </div>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Right Sidebar */}
        <AnimatePresence mode="wait">
          {selectedSensor ?
          <motion.div
            key="sensor-detail"
            initial={{
              opacity: 0,
              x: 50
            }}
            animate={{
              opacity: 1,
              x: 0
            }}
            exit={{
              opacity: 0,
              x: 50
            }}
            transition={{
              duration: 0.3
            }}
            className="w-[35%] flex-shrink-0 h-full">

              <SensorDetailSidebar
              sensor={selectedSensor}
              onUpdateSensor={(updatedSensor) => {
                setSelectedSensor(updatedSensor as any);
                setEditorZones(prevZones => prevZones.map(zone => ({
                  ...zone,
                  sensors: zone.sensors.map(s => s.id === updatedSensor.id ? { ...s, primary_appliance: updatedSensor.primary_appliance, appliance_names: updatedSensor.appliance_names } : s)
                })));
              }}
              onBack={handleSensorBack}
              onClose={handleZoomOut} />

            </motion.div> :
          isZoomed ?
          <motion.div
            key="zone-detail"
            initial={{
              opacity: 0,
              x: 50
            }}
            animate={{
              opacity: 1,
              x: 0
            }}
            exit={{
              opacity: 0,
              x: 50
            }}
            transition={{
              duration: 0.3
            }}
            className="w-[35%] flex-shrink-0 h-full">

              {currentRoom ? (
                <ZoneDetailSidebar
                  room={currentRoom}
                  onClose={handleZoomOut}
                  sensors={editorZones.find(z => z.id === currentRoom.id)?.sensors.map(s => ({ ...s, roomId: currentRoom.id })) || []}
                  onSelectSensor={handleSensorSelect} />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500 text-sm font-roboto">
                  {t('app.noSpaceData')}
                </div>
              )}

            </motion.div> :

          <motion.div
            key="energy-widgets"
            initial={{
              opacity: 0,
              x: 50
            }}
            animate={{
              opacity: 1,
              x: 0
            }}
            exit={{
              opacity: 0,
              x: 50
            }}
            transition={{
              duration: 0.3
            }}
            className="w-[35%] flex-shrink-0 flex flex-col gap-3 p-3 border-l border-white/20 bg-[#0a0f1a]/30 h-full overflow-y-auto">

              <motion.div
              initial={{
                opacity: 0,
                x: 20
              }}
              animate={{
                opacity: 1,
                x: 0
              }}
              transition={{
                delay: 0.2
              }}
              className="flex-[0.6] flex flex-col min-h-0">

                <EnergyChart currentAssetId={currentAssetId} />
              </motion.div>

              <motion.div
              initial={{
                opacity: 0,
                y: 15
              }}
              animate={{
                opacity: 1,
                y: 0
              }}
              transition={{
                delay: 0.3
              }}
              className="flex-[1.6] flex flex-col min-h-0">

                <MonthlyEnergyWidget currentAssetId={currentAssetId} />
              </motion.div>

              <motion.div
              initial={{
                opacity: 0,
                y: 15
              }}
              animate={{
                opacity: 1,
                y: 0
              }}
              transition={{
                delay: 0.4
              }}
              className="flex-[0.8] flex flex-col min-h-0">

                <TopOutletWidget seoSensors={allSeoSensors} />
              </motion.div>
            </motion.div>
          }
        </AnimatePresence>
      </div>

      {/* Floor Plan Editor */}
      <FloorPlanEditor
        isOpen={isEditorOpen}
        onClose={handleCloseEditor}
        onSave={handleSaveEditor}
        initialZones={editorZones}
        apartmentAssetId={currentAssetId}
        floorPlanUrl={floorPlanUrl}
        primaryZoneId={primaryZoneId}
        onChangePrimaryZone={setPrimaryZoneId}
      />
      {/* Curtain/Blind Controller */}
      <CurtainController
        isOpen={!!deviceToast && deviceToast.name.startsWith('SEC_')}
        deviceName={deviceToast?.name || null}
        alias={deviceToast?.alias || null}
        onClose={() => setDeviceToast(null)}
        sensors={allSensors}
      />

      {/* Device Message Dialog */}
      <AnimatePresence>
        {deviceToast && !deviceToast.name.startsWith('SEC_') &&
        <motion.div
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0
          }}
          transition={{
            duration: 0.2
          }}
          className="fixed inset-0 z-[300] flex items-center justify-center"
          onClick={() => setDeviceToast(null)}>
          
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }}
            onClick={(e) => e.stopPropagation()}
            className={`relative z-10 bg-slate-800/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden ${deviceToast.name === '전동블라인드' ? 'w-[90%] max-w-sm' : 'w-[85%] max-w-xs'}`}>
            
              <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
                <div
                className={`p-2 rounded-lg bg-cyan-500/20 text-cyan-400`}>
                
                  <LayoutGridIcon className="w-5 h-5" />
                </div>
                <div className="text-base font-bold text-white">
                  {deviceToast.alias}-{deviceToast.name}
                </div>
              </div>

              <div className="px-5 py-4">
                <p className="text-sm text-slate-300">{t('app.futureConnection')}</p>
              </div>
              <div className="px-5 pb-4">
                <button
                  onClick={() => setDeviceToast(null)}
                  className="w-full py-2.5 rounded-xl bg-cyan-500/20 text-cyan-400 text-sm font-bold hover:bg-cyan-500/30 transition-colors"
                >
                  {t('common.confirm')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>
      {/* Settings Modal */}
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />

      {/* Issue Board Modal */}
      <IssueBoardModal
        isOpen={isIssueBoardOpen}
        onClose={() => setIsIssueBoardOpen(false)}
        apartmentLabel={apartmentLabel || undefined}
        apartmentAssetId={currentAssetId || undefined}
      />
      </div>
    </ProportionalContainer>
  );
}